import Vue from "vue";
import axios from "@/plugins/axios";
import { formatProductChoiceLabel } from "@/utils/apiFilterParams";

const LEGACY_PILL_TYPES = new Set([
    "search",
    "boolean",
    "dynamicfilters",
    "country",
    "formatselect",
]);

const SEARCHABLE_TYPES = new Set([
    "searchselect",
    "searchcustomer",
    "searchprovider",
]);

/** In-memory id → label cache so remounted pills do not flash the raw id. */
const filterItemLabelCache = new Map();

function filterItemLabelCacheKey(filter, normalizedValue) {
    const endpoint = getFilterEndpoint(filter) || filter?.field || "";
    return `${endpoint}:${String(normalizedValue)}`;
}

export function getCachedFilterItemLabel(filter, value) {
    const normalized = normalizeFilterStoredValue(value, filter);
    if (
        normalized === null ||
        normalized === undefined ||
        normalized === "" ||
        typeof normalized === "object"
    ) {
        return null;
    }
    return filterItemLabelCache.get(filterItemLabelCacheKey(filter, normalized)) || null;
}

export function setCachedFilterItemLabel(filter, value, label) {
    const normalized = normalizeFilterStoredValue(value, filter);
    if (
        normalized === null ||
        normalized === undefined ||
        normalized === "" ||
        typeof normalized === "object" ||
        label === null ||
        label === undefined ||
        label === ""
    ) {
        return;
    }
    const text = String(label);
    if (text === String(normalized)) {
        return;
    }
    filterItemLabelCache.set(filterItemLabelCacheKey(filter, normalized), text);
}

/**
 * Coerce a filter value to the primitive stored in query params / API filters.
 * Handles pill payloads ({ value, label }), autocomplete items ({ text, value }), and { id, name } rows.
 */
export function normalizeFilterStoredValue(value, filter = null) {
    if (value === null || value === undefined || value === "") {
        return value;
    }
    if (Array.isArray(value)) {
        return value.map((item) => normalizeFilterStoredValue(item, filter));
    }
    if (typeof value !== "object") {
        return value;
    }
    if (Object.prototype.hasOwnProperty.call(value, "value")) {
        const nested = value.value;
        if (nested === null || nested === undefined || nested === "") {
            return null;
        }
        if (typeof nested === "object") {
            return normalizeFilterStoredValue(nested, filter);
        }
        return nested;
    }
    if (value.id !== null && value.id !== undefined && value.id !== "") {
        return value.id;
    }
    const valueField = filter?.value_field;
    if (valueField && value[valueField] !== undefined) {
        return value[valueField];
    }
    return value;
}

/**
 * Normalize a stored filter value for display / active-state checks.
 * Handles multi-select values stored as arrays or comma-separated strings.
 */
export function normalizeActiveFilterValue(value, filterconf) {
    if (value === null || value === undefined || value === "") {
        return null;
    }

    if (filterconf?.multiple) {
        if (Array.isArray(value)) {
            const items = value
                .map((item) => normalizeFilterStoredValue(item, filterconf))
                .filter(
                    (item) =>
                        item !== null && item !== undefined && item !== "",
                );
            return items.length ? items : null;
        }
        if (typeof value === "string") {
            const items = value
                .split(",")
                .map((part) => part.trim())
                .filter(Boolean);
            return items.length ? items : null;
        }
    }

    return normalizeFilterStoredValue(value, filterconf);
}

export function supportsPillPanel(filter) {
    return !!filter?.type && !LEGACY_PILL_TYPES.has(filter.type);
}

export function isTextInputFilter(filter) {
    return ["input", "string", "float", "integer"].includes(filter?.type);
}

/** Text/number metadata fields with a remote choices endpoint (e.g. productmetadata). */
export function isComboboxTextFilter(filter) {
    return isTextInputFilter(filter) && !!filter?.endpoint;
}

export function isNumberRangeFilter(filter) {
    return filter?.type === "numberrange";
}

export function isSliderFilter(filter) {
    return filter?.type === "slider";
}

export function isDateRangeFilter(filter) {
    return filter?.type === "daterange";
}

export function isDateTimeFilter(filter) {
    return filter?.type === "datetime" || filter?.type === "date";
}

export function isDateLikeFilter(filter) {
    return isDateRangeFilter(filter) || isDateTimeFilter(filter);
}

export function isPanelListFilter(filter) {
    if (!filter?.type) {
        return false;
    }
    if (
        isTextInputFilter(filter) ||
        isNumberRangeFilter(filter) ||
        isSliderFilter(filter) ||
        isDateLikeFilter(filter)
    ) {
        return false;
    }
    return supportsPillPanel(filter);
}

export function isRemoteFilter(filter) {
    return (
        SEARCHABLE_TYPES.has(filter?.type) ||
        !!filter?.endpoint
    );
}

/** Remote autocomplete types that refetch choices from the API as the user types. */
export function supportsRemoteSearch(filter) {
    return SEARCHABLE_TYPES.has(filter?.type);
}

export function shouldShowSearch(filter, choiceCount = 0) {
    if (SEARCHABLE_TYPES.has(filter?.type)) {
        return true;
    }
    if (filter?.endpoint) {
        return true;
    }
    return choiceCount > 8;
}

export function flattenSelectableChoices(choices) {
    return (choices || []).filter(
        (item) => item && !item.header && !item.divider && item.value !== undefined,
    );
}

export function resolveStaticChoices(filter, sourceChoices, ctx) {
    let choices = [];
    if (filter.choices) {
        if (typeof filter.choices === "function") {
            if (!sourceChoices) {
                return [];
            }
            choices = filter.choices(sourceChoices);
        } else {
            choices = [...filter.choices];
        }
    }
    if (filter.defaultLabel) {
        choices.unshift({
            value: null,
            text: filter.defaultLabel,
        });
    }
    if (filter.sort) {
        choices.sort((a, b) =>
            (a.text || "").toLowerCase().localeCompare((b.text || "").toLowerCase()),
        );
    }
    return choices;
}

function getFilterEndpoint(filter) {
    if (filter.type === "searchcustomer") {
        return "/api/customers/";
    }
    if (filter.type === "searchprovider") {
        return "/api/providers/";
    }
    return filter.endpoint || null;
}

const ACTIVE_FILTER_SKIP_KEYS = new Set([
    "products",
    "limit",
    "offset",
    "ordering",
    "paginate",
]);

function mergeActiveFiltersForMetadata(filter, params, activeFilters) {
    if (!activeFilters || filter.source !== "productmetadata") {
        return params;
    }
    const merged = { ...params };
    const currentField = filter.field;
    Object.entries(activeFilters).forEach(([key, value]) => {
        if (ACTIVE_FILTER_SKIP_KEYS.has(key)) {
            return;
        }
        if (key === currentField) {
            return;
        }
        if (key.endsWith("_min") || key.endsWith("_max")) {
            return;
        }
        if (value === null || value === undefined || value === "") {
            return;
        }
        if (Array.isArray(value) || typeof value === "object") {
            return;
        }
        if (merged[key] === undefined) {
            merged[key] = value;
        }
    });
    return merged;
}

export const REMOTE_CHOICES_PAGE_SIZE = 20;
export const REMOTE_CHOICES_PREFETCH_PX = 120;

export function supportsRemoteChoicePaging(filter) {
    return SEARCHABLE_TYPES.has(filter?.type);
}

export function shouldPrefetchRemoteChoices({
    scrollTop,
    clientHeight,
    scrollHeight,
    prefetchPx = REMOTE_CHOICES_PREFETCH_PX,
}) {
    if (!clientHeight || !scrollHeight) {
        return false;
    }
    const remaining = scrollHeight - scrollTop - clientHeight;
    return remaining <= prefetchPx;
}

export function remoteChoicesPageHasMore(data, { offset = 0, limit, rowCount } = {}) {
    if (!data || rowCount === 0) {
        return false;
    }
    if (data.next) {
        return true;
    }
    if (typeof data.count === "number") {
        return offset + rowCount < data.count;
    }
    if (typeof limit === "number" && limit > 0) {
        return rowCount >= limit;
    }
    return false;
}

function getRemoteChoiceRowCount(data, filter) {
    if (!data) {
        return 0;
    }
    if (filter?.source === "productmetadata") {
        return Array.isArray(data.values) ? data.values.length : 0;
    }
    if (filter?.source === "catalogentry_category") {
        return Array.isArray(data) ? data.length : 0;
    }
    if (Array.isArray(data.results)) {
        return data.results.length;
    }
    return 0;
}

function applyFilterChoiceTransform(choices, filter, ctx) {
    if (typeof filter?.transform === "function") {
        return filter.transform(choices);
    }
    if (filter?.source === "catalog") {
        return groupCatalogChoices(choices, {
            myCatalogsLabel: ctx?.myCatalogsLabel,
            agencyCatalogsLabel: ctx?.agencyCatalogsLabel,
        });
    }
    return choices;
}

export function mergeFilterChoicePages(existing, incoming, filter, ctx = {}) {
    const incomingSelectable = flattenSelectableChoices(incoming);
    if (!incomingSelectable.length) {
        return existing || [];
    }
    const existingSelectable = flattenSelectableChoices(existing);
    const seen = new Set(
        existingSelectable.map((choice) => String(choice.value)),
    );
    const toAdd = incomingSelectable.filter(
        (choice) => !seen.has(String(choice.value)),
    );
    if (!toAdd.length) {
        return existing || [];
    }
    if (typeof filter?.transform === "function" || filter?.source === "catalog") {
        return applyFilterChoiceTransform(
            existingSelectable.concat(toAdd),
            filter,
            ctx,
        );
    }
    return (existing || []).concat(toAdd);
}

export function transformApiResponseToChoices(data, filter, ctx = {}, options = {}) {
    const translate = ctx.translate || ((key) => Vue.prototype.$translate(key));
    const utils = ctx.utils;
    const currentEntityId = ctx.currentEntityId;
    const offset = options.offset || 0;

    let choices = [];
    if (filter.defaultLabel && offset === 0) {
        choices.push({
            value: null,
            text: filter.defaultLabel,
        });
    }
    if (!filter.source) {
        (data.results || []).forEach((item) => {
            choices.push({
                text: utils.traverse(item, filter.text_field || "id"),
                value: utils.traverse(item, filter.value_field || "id"),
            });
        });
        return choices;
    }
    switch (filter.source) {
        case "user": {
            if (offset === 0) {
                choices.push({
                    text: translate("noUser"),
                    value: "null",
                });
            }
            const internalUsers = (data.results || []).filter(
                (item) => !item.entity || item.entity === currentEntityId,
            );
            const externalUsers = (data.results || []).filter(
                (item) => item.entity && item.entity !== currentEntityId,
            );
            internalUsers.forEach((item) => {
                choices.push({ text: item.full_name, value: item.id });
            });
            if (externalUsers.length > 0) {
                choices.push({ header: translate("agents") });
                externalUsers.forEach((item) => {
                    const entityName = item.entity_name || item.entity_data?.name;
                    choices.push({
                        text: entityName
                            ? `${item.full_name} (${entityName})`
                            : item.full_name,
                        value: item.id,
                    });
                });
            }
            break;
        }
        case "catalog":
            (data.results || []).forEach((item) => {
                choices.push(buildCatalogChoice(item, ctx));
            });
            break;
        case "vendor":
            (data.results || []).forEach((item) => {
                choices.push({
                    text: item.entity_data?.name,
                    value: item.entity,
                });
            });
            break;
        case "provider":
        case "entity":
        case "customer":
        case "buyer":
        case "initiator":
        case "recipient":
        case "deliverysite":
        case "billingaccount":
            (data.results || []).forEach((item) => {
                choices.push({ text: item.name, value: item.id });
            });
            break;
        case "product":
            (data.results || []).forEach((item) => {
                choices.push({
                    text: formatProductChoiceLabel(item),
                    value: item.id,
                });
            });
            break;
        case "producttype":
            (data.results || []).forEach((item) => {
                choices.push({ text: item.label, value: item.id });
            });
            break;
        case "storage_location":
            (data.results || []).forEach((warehouse) => {
                if (!warehouse.storage_locations_enabled) return;
                if (filter.warehouseId && String(warehouse.id) !== String(filter.warehouseId)) return;
                (warehouse.storage_locations || []).forEach((location) => {
                    choices.push({ value: location.id, text: `${warehouse.name} · ${location.name}` });
                });
            });
            break;
        case "stock":
            (data.results || []).forEach((item) => {
                if (item.owned) {
                    choices.push({ value: item.id, text: item.name });
                } else {
                    choices.push({
                        value: item.id,
                        text: `${item.owner_data.name} - ${item.name}`,
                    });
                }
            });
            break;
        case "stock_owner": {
            choices.push({
                text: data.entity_name,
                value: "entity",
            });
            const providerItems = (data.providers || []).filter(
                (p) => p && p.id != null,
            );
            if (providerItems.length > 0) {
                choices.push({
                    header: translate("inventoryOwnerSuppliers"),
                });
                providerItems.forEach((p) => {
                    choices.push({
                        text: p.name,
                        value: `provider_${p.id}`,
                    });
                });
            }
            break;
        }
        case "productmetadata":
            (data.values || []).forEach((item) => {
                choices.push({
                    text: (item.text || item.value) + (filter.unit ? ` ${filter.unit}` : ""),
                    value: item.value,
                });
            });
            break;
        case "catalogentry_category":
            (data || []).forEach((item) => {
                choices.push({ text: item, value: item });
            });
            break;
        case "tags":
            (data.results || []).forEach((tag) => {
                choices.push({
                    text: `${tag.name} (${tag.usage_count || 0})`,
                    value: tag.name,
                    color: tag.color || "#9E9E9E",
                });
            });
            break;
        default:
            break;
    }
    return choices;
}

export function createChoiceContext(vm) {
    const ctx = {
        utils: vm.$utils,
        translate: (key) => vm.$translate(key),
        currentEntityId: vm.current_entity_id,
    };
    if (typeof vm.$translate === "function") {
        const myKey = "filters.myCatalogs";
        const agencyKey = "filters.agencyCatalogs";
        const myTranslated = vm.$translate(myKey);
        const agencyTranslated = vm.$translate(agencyKey);
        if (myTranslated !== myKey) {
            ctx.myCatalogsLabel = myTranslated;
        }
        if (agencyTranslated !== agencyKey) {
            ctx.agencyCatalogsLabel = agencyTranslated;
        }
    }
    return ctx;
}

export function isProviderConnected(providerData) {
    if (!providerData) {
        return false;
    }
    return Boolean(providerData.entity);
}

/**
 * Build a catalog filter/autocomplete choice with structured seller/catalog labels.
 */
export function buildCatalogChoice(item, ctx = {}) {
    const translate = ctx.translate || ((key) => key);
    const catalogName = item.name || translate("unnamed");
    const currentEntityId = ctx.currentEntityId;

    if (item.provider_data?.name) {
        return {
            text: `${item.provider_data.name} - ${catalogName}`,
            value: item.id,
            category: "agency",
            sellerName: item.provider_data.name,
            catalogName,
            sellerConnected: isProviderConnected(item.provider_data),
        };
    }

    const isOwnCatalog =
        currentEntityId != null &&
        item.owner != null &&
        item.owner === currentEntityId &&
        !item.provider;

    if (isOwnCatalog) {
        return {
            text: catalogName,
            value: item.id,
            category: "owned",
            catalogName,
            sellerName: null,
            sellerConnected: false,
        };
    }

    const ownerName = item.owner_data?.name;
    if (ownerName) {
        return {
            text: `${ownerName} - ${catalogName}`,
            value: item.id,
            category: "agency",
            sellerName: ownerName,
            catalogName,
            sellerConnected: false,
        };
    }

    return {
        text: catalogName,
        value: item.id,
        category: "owned",
        catalogName,
        sellerName: null,
        sellerConnected: false,
    };
}

export function isCatalogChoiceDisplay(filter, choice) {
    return (
        filter?.source === "catalog" &&
        choice &&
        !choice.header &&
        !choice.divider &&
        String(choice.catalogName || "").trim() !== ""
    );
}

export function hasFilterChoiceDisplayLabel(filter, choice) {
    if (!choice || choice.header || choice.divider) {
        return Boolean(choice?.header);
    }
    if (isCatalogChoiceDisplay(filter, choice)) {
        return true;
    }
    return String(choice.text || "").trim() !== "";
}

function copyCatalogChoiceEntry(choice) {
    const entry = {
        text: choice.text,
        value: choice.value,
    };
    if (choice.catalogName != null) {
        entry.catalogName = choice.catalogName;
    }
    if (choice.sellerName) {
        entry.sellerName = choice.sellerName;
    }
    if (choice.sellerConnected) {
        entry.sellerConnected = choice.sellerConnected;
    }
    if (choice.category) {
        entry.category = choice.category;
    }
    return entry;
}

export function compareCatalogChoices(a, b) {
    const sellerA = (a.sellerName || "").toLowerCase();
    const sellerB = (b.sellerName || "").toLowerCase();
    if (sellerA !== sellerB) {
        return sellerA.localeCompare(sellerB);
    }
    const catalogA = (a.catalogName || a.text || "").toLowerCase();
    const catalogB = (b.catalogName || b.text || "").toLowerCase();
    return catalogA.localeCompare(catalogB);
}

/**
 * Group catalog filter choices: owned catalogs first, then agency catalogs,
 * each section with a header and sorted by seller name then catalog name.
 */
export function groupCatalogChoices(
    choices,
    { myCatalogsLabel = "My catalogs", agencyCatalogsLabel = "Agency catalogs" } = {},
) {
    if (!choices || !choices.length) {
        return [];
    }

    const owned = [];
    const agency = [];

    choices.forEach((choice) => {
        if (choice.header || choice.divider) {
            return;
        }
        if (choice.value === null || choice.value === undefined) {
            return;
        }
        const entry = copyCatalogChoiceEntry(choice);
        if (!String(entry.catalogName || entry.text || "").trim()) {
            return;
        }
        if (choice.category === "agency") {
            agency.push(entry);
        } else {
            owned.push(entry);
        }
    });

    owned.sort(compareCatalogChoices);
    agency.sort(compareCatalogChoices);

    const result = [];
    if (owned.length) {
        result.push({ header: myCatalogsLabel });
        result.push(...owned);
    }
    if (agency.length) {
        result.push({ header: agencyCatalogsLabel });
        result.push(...agency);
    }
    return result.length ? result : owned;
}

export function catalogChoiceMatchesQuery(choice, query) {
    const normalized = String(query || "").trim().toLowerCase();
    if (!normalized) {
        return true;
    }
    const haystack = [choice.text, choice.sellerName, choice.catalogName]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
    return haystack.includes(normalized);
}

export function filterChoiceMatchesQuery(filter, choice, query) {
    if (!query) {
        return true;
    }
    const normalizedQuery = String(query).trim().toLowerCase();
    if (!normalizedQuery) {
        return true;
    }
    if (isCatalogChoiceDisplay(filter, choice)) {
        return catalogChoiceMatchesQuery(choice, normalizedQuery);
    }
    return String(choice.text || choice.value || "")
        .toLowerCase()
        .includes(normalizedQuery);
}

function formatCatalogChoiceLabel(item, ctx) {
    return buildCatalogChoice(item, ctx).text;
}

function extractItemLabel(item, filter, ctx) {
    switch (filter.source) {
        case "user":
            return item.full_name;
        case "producttype":
            return item.label;
        case "product":
            return formatProductChoiceLabel(item);
        case "productmetadata":
            return item.text || item.value;
        case "catalogentry_category":
            return item;
        case "catalog":
            return formatCatalogChoiceLabel(item, ctx);
        default: {
            if (filter?.text_field && item && typeof item === "object") {
                const viaField = item[filter.text_field];
                if (viaField != null && viaField !== "") {
                    return viaField;
                }
            }
            return item.name || item.label || item.full_name;
        }
    }
}

export function needsRemoteLabelResolution(filter, value) {
    const normalized = normalizeFilterStoredValue(value, filter);
    if (normalized === null || normalized === undefined || normalized === "") {
        return false;
    }
    if (typeof normalized === "object") {
        return false;
    }
    if (filter.source === "productmetadata") {
        const metadataField = getProductMetadataField(filter);
        return metadataField === "producttype" || filter.field === "producttype";
    }
    if (SEARCHABLE_TYPES.has(filter?.type)) {
        return true;
    }
    // Endpoint-backed selects (e.g. catalogFormat / format) often have no
    // `source`; still resolve labels via GET `${endpoint}${id}/`.
    if (filter?.endpoint) {
        return true;
    }
    return false;
}

function getProductMetadataField(filter) {
    return filter?.params?.field || filter?.field;
}

function resolveStockOwnerChoiceLabel(data, normalizedValue) {
    if (!data || normalizedValue == null || normalizedValue === "") {
        return null;
    }
    const value = String(normalizedValue);
    if (value === "entity") {
        return data.entity_name || null;
    }
    const providerMatch = value.match(/^provider_(\d+)$/);
    if (providerMatch) {
        const providerId = Number(providerMatch[1]);
        const provider = (data.providers || []).find(
            (item) => item && Number(item.id) === providerId,
        );
        return provider?.name || null;
    }
    return null;
}

async function fetchStockOwnerLabel(filter, normalizedValue) {
    const endpoint = getFilterEndpoint(filter);
    if (!endpoint) {
        return null;
    }
    try {
        const response = await axios.get(endpoint, {
            params: { ...(filter.params || {}), limit: 10000 },
        });
        return resolveStockOwnerChoiceLabel(response.data, normalizedValue);
    } catch {
        return null;
    }
}

export async function fetchFilterItemLabel(filter, value, ctx = {}) {
    const normalizedValue = normalizeFilterStoredValue(value, filter);
    if (
        normalizedValue === null ||
        normalizedValue === undefined ||
        normalizedValue === "" ||
        typeof normalizedValue === "object"
    ) {
        return null;
    }
    if (filter.source === "user" && String(normalizedValue) === "null") {
        return ctx.translate?.("noUser") || "No user";
    }
    if (Array.isArray(normalizedValue)) {
        const labels = await Promise.all(
            normalizedValue.map((item) => fetchFilterItemLabel(filter, item, ctx)),
        );
        return labels.filter(Boolean).join(", ");
    }

    const endpoint = getFilterEndpoint(filter);
    if (!endpoint) {
        return null;
    }

    if (filter.source === "storage_location") {
        try {
            const choices = await fetchFilterChoices(filter, { ctx });
            const match = choices.find(choice => String(choice.value) === String(normalizedValue));
            if (match) setCachedFilterItemLabel(filter, normalizedValue, match.text);
            return match?.text || null;
        } catch {
            return null;
        }
    }

    if (filter.source === "stock_owner") {
        return fetchStockOwnerLabel(filter, normalizedValue);
    }

    if (filter.source === "productmetadata") {
        if (getProductMetadataField(filter) === "producttype") {
            try {
                const response = await axios.get(
                    `/api/producttypes/${normalizedValue}/`,
                );
                const label = extractItemLabel(
                    response.data,
                    { source: "producttype" },
                    ctx,
                );
                if (label) {
                    return label;
                }
            } catch {
                // fall through to metadata list lookup
            }
            try {
                const response = await axios.get(endpoint, {
                    params: { ...(filter.params || {}), limit: 10000 },
                });
                const match = (response.data?.values || []).find(
                    (item) => String(item.value) === String(normalizedValue),
                );
                if (match?.text) {
                    return match.text;
                }
            } catch {
                return null;
            }
        }
        return String(normalizedValue);
    }

    try {
        const base = endpoint.endsWith("/") ? endpoint : `${endpoint}/`;
        const response = await axios.get(`${base}${normalizedValue}/`);
        const label = extractItemLabel(response.data, filter, ctx);
        if (label) {
            setCachedFilterItemLabel(filter, normalizedValue, label);
        }
        return label;
    } catch {
        return null;
    }
}

/**
 * @typedef {Object} FilterChoicesPage
 * @property {Array<Object>} choices
 * @property {boolean} hasMore
 * @property {number} nextOffset
 * @property {number} pageSize
 */

export async function fetchFilterChoicesPage(
    filter,
    { search = null, ctx, activeFilters = null, offset = 0 } = {},
) {
    if (filter.choices && !filter.endpoint && !SEARCHABLE_TYPES.has(filter.type)) {
        const choices = resolveStaticChoices(filter, null, ctx);
        return {
            choices,
            hasMore: false,
            nextOffset: choices.length,
            pageSize: choices.length,
        };
    }

    const endpoint = getFilterEndpoint(filter);
    if (!endpoint) {
        const choices = resolveStaticChoices(filter, null, ctx);
        return {
            choices,
            hasMore: false,
            nextOffset: choices.length,
            pageSize: choices.length,
        };
    }

    const paged = supportsRemoteChoicePaging(filter);
    const pageOffset = paged ? Math.max(0, Number(offset) || 0) : 0;
    let params = mergeActiveFiltersForMetadata(
        filter,
        { ...(filter.params || {}) },
        activeFilters,
    );
    if (paged) {
        params = {
            [filter.searchParam || "search"]: search || undefined,
            limit: REMOTE_CHOICES_PAGE_SIZE,
            ...params,
            offset: pageOffset,
        };
    } else {
        params = { ...params, limit: 10000 };
    }

    if (filter.source === "tags") {
        if (!filter.object_type) {
            throw new Error("Tags filter requires object_type to be specified");
        }
        params.object_type = filter.object_type;
    }

    const response = await axios.get(endpoint, { params });
    const data = response.data;
    let choices = transformApiResponseToChoices(data, filter, ctx, {
        offset: pageOffset,
    });
    choices = applyFilterChoiceTransform(choices, filter, ctx);

    const rowCount = getRemoteChoiceRowCount(data, filter);
    const pageSize = Number(params.limit);
    const hasMore = paged
        ? remoteChoicesPageHasMore(data, {
              offset: pageOffset,
              limit: pageSize,
              rowCount,
          })
        : false;
    const nextOffset =
        pageOffset +
        (Number.isFinite(pageSize) && pageSize > 0 ? pageSize : rowCount);

    return {
        choices,
        hasMore,
        nextOffset,
        pageSize: Number.isFinite(pageSize) ? pageSize : rowCount,
    };
}

export async function fetchFilterChoices(filter, options = {}) {
    const page = await fetchFilterChoicesPage(filter, options);
    return page.choices;
}
